import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { Loader2 } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { ContactTableRow } from '@/components/postgrid/contact-table/ContactTableRow';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { ReadPostgridContact } from '@/services/api/generated/postgridService';

interface ContactTableProps {
  contacts: ReadPostgridContact[];
  isLoading?: boolean;
  error?: string;
  onContactView?: (contact: ReadPostgridContact) => void;
  onContactDelete?: (contact: ReadPostgridContact) => void;
  onContactRetrySync?: (contact: ReadPostgridContact) => void;
  className?: string;
}

interface SimpleFilters {
  search?: string;
  status?: string;
}

type SortField = 'name' | 'address';
type SortDirection = 'asc' | 'desc';

// Helper function to get display name
const getContactDisplayName = (contact: ReadPostgridContact): string => {
  const firstName = contact.first_name?.trim() || '';
  const lastName = contact.last_name?.trim() || '';
  const companyName = contact.company_name?.trim() || '';

  if (firstName || lastName) {
    const fullName = `${firstName} ${lastName}`.trim();
    return companyName ? `${fullName} (${companyName})` : fullName;
  }

  return companyName || 'Unnamed Contact';
};

// Helper function to get address display
const getAddressDisplay = (contact: ReadPostgridContact): string => {
  const parts = [
    contact.address_line1,
    contact.city,
    contact.province_or_state,
  ].filter(Boolean);
  return parts.join(', ') || 'No address';
};

// Filter function
const filterContacts = (
  contacts: ReadPostgridContact[],
  filters: SimpleFilters,
): ReadPostgridContact[] => {
  return contacts.filter((contact) => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const searchableText = [
        contact.first_name,
        contact.last_name,
        contact.company_name,
        contact.address_line1,
        contact.city,
        contact.province_or_state,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (!searchableText.includes(searchLower)) {
        return false;
      }
    }

    return true;
  });
};

// Sort function
const sortContacts = (
  contacts: ReadPostgridContact[],
  sortField: SortField,
  sortDirection: SortDirection,
): ReadPostgridContact[] => {
  return [...contacts].sort((a, b) => {
    let aValue: string;
    let bValue: string;

    switch (sortField) {
      case 'name':
        aValue = getContactDisplayName(a).toLowerCase();
        bValue = getContactDisplayName(b).toLowerCase();
        break;
      case 'address':
        aValue = getAddressDisplay(a).toLowerCase();
        bValue = getAddressDisplay(b).toLowerCase();
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
};

// Sort button component
const SortButton: React.FC<{
  field: SortField;
  children: React.ReactNode;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}> = ({ field, children, sortField, sortDirection, onSort }) => (
  <Button
    onClick={() => onSort(field)}
    variant="ghost"
    className="h-auto p-0 font-medium text-left justify-start hover:bg-transparent text-gray-600 hover:text-gray-900 -ml-1"
  >
    <span>{children}</span>
    {sortField === field && (
      <span className="ml-1">
        {sortDirection === 'asc' ? (
          <ArrowUpIcon className="h-3 w-3" />
        ) : (
          <ArrowDownIcon className="h-3 w-3" />
        )}
      </span>
    )}
  </Button>
);

const ContactTable = ({
  contacts,
  isLoading = false,
  error,
  onContactView,
  onContactDelete,
  onContactRetrySync,
  className,
}: ContactTableProps) => {
  const [filters, setFilters] = useState<SimpleFilters>({});
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSearchChange = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
  };

  // Process data
  const processedContacts = useMemo(() => {
    const filtered = filterContacts(contacts, filters);
    return sortContacts(filtered, sortField, sortDirection);
  }, [contacts, filters, sortField, sortDirection]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span className="text-sm text-gray-600">Loading contacts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const emptyMessage = (() => {
    const hasSearch = filters.search;
    if (hasSearch) return 'No contacts match your search';
    return 'No contacts have been created yet.';
  })();

  return (
    <div className={cn('space-y-4', className)}>
      {/* Search and Results Summary */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Input
            placeholder="Search by name, company, or address..."
            value={filters.search || ''}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pr-10"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => handleSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
        <div className="text-sm text-gray-500">
          {processedContacts.length} contact
          {processedContacts.length !== 1 ? 's' : ''}
          {filters.search && ` of ${contacts.length} total`}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-b border-gray-100 bg-gray-50/50">
              <TableHead className="font-medium text-gray-600 text-xs uppercase tracking-wide py-3 pl-4">
                <SortButton
                  field="name"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Contact
                </SortButton>
              </TableHead>
              <TableHead className="font-medium text-gray-600 text-xs uppercase tracking-wide py-3">
                <SortButton
                  field="address"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                >
                  Address
                </SortButton>
              </TableHead>
              <TableHead className="w-[60px] pr-4" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {processedContacts.length === 0 ? (
              <TableRow>
                <td colSpan={3} className="text-center py-12">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-gray-900">
                      No contacts found
                    </div>
                    <div className="text-sm text-gray-500">{emptyMessage}</div>
                  </div>
                </td>
              </TableRow>
            ) : (
              processedContacts.map((contact) => (
                <ContactTableRow
                  key={contact.id ?? `contact-${Math.random()}`}
                  contact={contact}
                  onView={onContactView}
                  onDelete={onContactDelete}
                  onRetrySync={onContactRetrySync}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export { ContactTable };
